/**
 * Maps the antd Table `sorter` from `onChange` onto the server's
 * `sort: { field, order }` input. A column key the server cannot order by
 * (or a third click that clears the sort) means "no sort", so the list keeps
 * its default order.
 */
export const toServerSort = (sorter, allowedKeys) => {
  const key = sorter?.columnKey;
  if (!key || !sorter.order || !allowedKeys.includes(key)) return null;
  return { field: key, order: sorter.order === "ascend" ? "asc" : "desc" };
};

// Compares text the way people read a list: case-insensitive, numbers by
// value ("Branch 2" before "Branch 10"), blanks first.
const collator = new Intl.Collator("en", { sensitivity: "base", numeric: true });
export const compareText = (a, b) => {
  const left = a ?? "";
  const right = b ?? "";
  if (!left || !right) return left ? 1 : right ? -1 : 0;
  return collator.compare(String(left), String(right));
};

/** True when `text` is blank or appears in any of the record's `fields`. */
export const matchesSearch = (record, fields, text) => {
  const needle = (text ?? "").trim().toLowerCase();
  if (!needle) return true;
  return fields.some((field) =>
    String(record?.[field] ?? "").toLowerCase().includes(needle)
  );
};

/** Numbers low to high; missing values first. */
export const compareNumber = (a, b) => {
  const left = Number.isFinite(a) ? a : null;
  const right = Number.isFinite(b) ? b : null;
  if (left === null || right === null) return left === null ? (right === null ? 0 : -1) : 1;
  return left - right;
};

// The API sends dates as epoch-millisecond strings ("1700000000000"); older
// fields may be ISO strings. Anything unreadable counts as missing.
const toTime = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) return asNumber;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Dates oldest first; missing or unreadable dates first. */
export const compareDate = (a, b) => compareNumber(toTime(a), toTime(b));

/**
 * Orders rows the way antd's Table would for `sortedInfo` ({ columnKey,
 * order }), using that column's own `sorter` — so a phone card list sorts
 * exactly like the table's header. No match or no order: rows as they came.
 */
export const sortByColumn = (rows, columns, { columnKey, order } = {}) => {
  const column = columns.find((c) => (c.key ?? c.dataIndex) === columnKey);
  const compare = typeof column?.sorter === "function" ? column.sorter : null;
  if (!compare || !order) return rows;
  const direction = order === "descend" ? -1 : 1;
  return [...rows].sort((a, b) => direction * compare(a, b));
};
